import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Grid,
  InputAdornment,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useStyles } from "../../theme/appStyles";
import {
  useCreatePolicySummaryReportMutation,
  useGetAllMainCategoryQuery,
  useGetAllPolicyGropByQuery,
  useGetCustomerQuery,
  useGetProductQuery,
  useCreatePolicySummaryDescriptionMutation,
  useCreatePolicySummaryRemarkMutation,
  useGetlastUpdateRemarkQuery,
} from "../../Api/attoDeskApi";
import {
  ICategory,
  ICustomer,
  IPolicy,
  IPolicyTerm,
  IProductPopUP,
} from "../../Api/Interface/api.interface";
import { useNotifier } from "../../Core/Notifier";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useFormik } from "formik";
import LocalPrintshopSharpIcon from "@mui/icons-material/LocalPrintshopSharp";
import { formatPhoneNumberForDisplaySpace } from "../../Components/Validation/validationUtils";
import AvatarImg from "../../Images/avatar.png";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const steps = ["Select Policy", "verify Terms", "Done"];
const capitalizeFirstLetter = (name: string | null | undefined) => {
  if (!name) return "";
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
};

type PolicySummaryFormValues = {
  id: number | null;
  policySummaryTerms: string[]; // or number[] if terms are IDs
  policySummaryRemark: string;
};

interface Policy {
  id: number;
  policyNumber: string;
  policyCategoryId: number;
  policyProductId: number;
  // add other fields if needed
}

interface PolicyGroupData {
  [policyNumber: string]: Policy[];
}

interface Props {
  customerId: number | string;
}

const Index = () => {
  const classes = useStyles();
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);

  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const gridHeight = isMobile ? "30px" : "400px";

  const navigate = useNavigate();

  const { id: customerId } = useParams<{ id: string }>();
  const { showErrorMessage, showMessage } = useNotifier();
  const [addedPolicyTerm, setAddedPolicyTerm] = useState<IPolicyTerm[]>([]);
  const [polisySummaryId, setPolisySummaryId] = useState<number | null>(null);

  useEffect(() => {}, [customerId]);

  const { data, isLoading, isError } = useGetCustomerQuery({
    searchText: "",
    id: String(customerId),
  });

  const {
    data: customerProjectData,
    isLoading: customerProjectDataIsLoading,
    refetch: cutomerProjectRefetch,
  } = useGetAllPolicyGropByQuery({
    searchText: "",
    id: "",
    policyCustomerId: String(customerId),
  });

  const {
    data: lastUpdateRemarkData,
    isLoading: lastUpdateRemarkIsLoading,
    refetch: lastUpdateRemarkRefetch,
  } = useGetlastUpdateRemarkQuery({
    policySummaryCustomerId: String(customerId),
    id: String(polisySummaryId),
  });

  const { data: categoryData } = useGetAllMainCategoryQuery({
    departmentId: "",
    coursingId: "",
    roleId: null,
    taxeId: "",
    createdDateStart: null,
    createdDateEnd: null,
    searchText: "",
    numberOfLength: 0,
    isActive: true,
    categoryIsLooping: null,
    createdDate: null,
  });

  const {
    data: productData,
    isLoading: productDataIsLoading,
    isError: productDataError,
  } = useGetProductQuery({
    searchText: "",
    id: "",
    productBrandId: null,
    productCategoryId: null,
    productTagIds: null,
    productViewOnline: null,
    createdDateStart: null,
    createdDateEnd: null,
    createdDate: null,
  });

  const [newPolicy, { isLoading: dataIsLoading }] =
    useCreatePolicySummaryReportMutation();

  const [newDescription, { isLoading: dataDescriptionIsLoading }] =
    useCreatePolicySummaryDescriptionMutation();

  const [newRemark, { isLoading: dataRemarkIsLoading }] =
    useCreatePolicySummaryRemarkMutation();

  const policyGroups = (customerProjectData?.data ?? {}) as PolicyGroupData;

  const customer = useMemo(() => {
    if (data && Array.isArray(data.data) && data.data.length > 0) {
      return data.data[0] as ICustomer;
    }
    return null;
  }, [data]);

  // const policyList = useMemo(() => {
  //   return (customerProjectData?.data || []) as IPolicy[];
  // }, [customerProjectData?.data]);

  const categoryList = useMemo(() => {
    return categoryData?.data as ICategory[];
  }, [categoryData?.data]);

  const productList = useMemo(() => {
    return productData?.data as IProductPopUP[];
  }, [productData?.data]);

  const categoryMap = useMemo(() => {
    const map = new Map();
    categoryList?.forEach((brand) => {
      map.set(brand.id, brand.categoryName);
    });
    return map;
  }, [categoryList]);

  const productMap = useMemo(() => {
    const map = new Map();
    productList?.forEach((brand) => {
      map.set(brand.id, brand.productName);
    });
    return map;
  }, [productList]);

  const [selectedPolicies, setSelectedPolicies] = useState<number[]>([]);

  // useEffect(() => {
  //   if (policyList.length > 0) {
  //     setSelectedPolicies(policyList.map((policy) => policy.id));
  //   }
  // }, [policyList]);

  useEffect(() => {
    if (policyGroups && Object.keys(policyGroups).length > 0) {
      const allPolicyIds = Object.values(policyGroups)
        .flat()
        .map((p) => p.id);

      setSelectedPolicies(allPolicyIds);
    }
  }, [policyGroups]);

  const handlePolicyChange = (policyId: number) => {
    setSelectedPolicies((prevSelected) =>
      prevSelected.includes(policyId)
        ? prevSelected.filter((id) => id !== policyId)
        : [...prevSelected, policyId]
    );
  };

  const handleParentSelect = (policyNumber: string) => {
    const childIds = policyGroups[policyNumber].map((p) => p.id);
    const allSelected = childIds.every((id) => selectedPolicies.includes(id));

    setSelectedPolicies(
      (prev) =>
        allSelected
          ? prev.filter((id) => !childIds.includes(id)) // unselect all
          : [...prev, ...childIds.filter((id) => !prev.includes(id))] // select all
    );
  };

  const handleChildSelect = (policyId: number) => {
    setSelectedPolicies((prev) =>
      prev.includes(policyId)
        ? prev.filter((id) => id !== policyId)
        : [...prev, policyId]
    );
  };

  const [checkedPolicies, setCheckedPolicies] = useState<{
    [key: string]: boolean;
  }>({});

  useEffect(() => {
    const initialCheckedPolicies: { [key: string]: boolean } = {};
    addedPolicyTerm.forEach((term) => {
      term.policyTermsArray.forEach((item) => {
        const checkboxKey = `${term.id}-${item.policyTermsDescription}`;
        if (item.isPolicyTermsSummaryReport || item.isPolicyTermsAll) {
          initialCheckedPolicies[checkboxKey] = true;
        } else {
          initialCheckedPolicies[checkboxKey] = false;
        }
      });
    });
    setCheckedPolicies(initialCheckedPolicies);
  }, [addedPolicyTerm]);

  const handleCheckboxChange = (
    termId: number | string,
    description: string
  ) => {
    const checkboxKey = `${termId}-${description}`;
    setCheckedPolicies((prevChecked) => ({
      ...prevChecked,
      [checkboxKey]: !prevChecked[checkboxKey],
    }));
  };

  // const preparePayload = () => {
  //   return addedPolicyTerm
  //     .map((term) => ({
  //       policyTermProductId: term.policyTermProductId,
  //       policyTermsArray: term.policyTermsArray.filter(
  //         (item) => checkedPolicies[`${term.id}-${item.policyTermsDescription}`]
  //       ),
  //     }))
  //     .filter((term) => term.policyTermsArray.length > 0);
  // };

  const preparePayload = () => {
    const mergedPolicyTermsArray = addedPolicyTerm.flatMap(
      (term) =>
        term.policyTermsArray
          .filter(
            (item) =>
              checkedPolicies[`${term.id}-${item.policyTermsDescription}`]
          )
          .map((item) => item.id) 
    );

    return mergedPolicyTermsArray; 
  };

  const policySummaryTermsPayload = preparePayload();

  const uniquePolicyProductIds = Array.from(
    new Set(
      Object.values(policyGroups)
        .flat() 
        .filter((policy) => selectedPolicies.includes(policy.id))
        .map((policy) => policy.policyProductId)
    )
  );  

  const formik = useFormik({
    initialValues: {
      policySummaryCustomerId: customerId,
      policySummaryIds: [],
      policySummaryProductIds: [],
    },

    onSubmit: async (values, { resetForm }) => {
      try {
        const temData = {
          policySummaryCustomerId: values.policySummaryCustomerId,
          policySummaryIds: selectedPolicies,
          policySummaryProductIds: uniquePolicyProductIds,
        };
        const addCompanyResponse = await newPolicy(temData).unwrap();
        if (!addCompanyResponse.status) {
          showErrorMessage(addCompanyResponse.message);
        } else {
          showMessage(addCompanyResponse.message);
          setAddedPolicyTerm(addCompanyResponse.data);
          const summaryDataId = Number(addCompanyResponse.summaryData.id);
          setPolisySummaryId(Number(summaryDataId));
          resetForm();
          
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  console.log(lastUpdateRemarkData?.data.policySummaryRemark);

  const formikStep2 = useFormik({
    initialValues: {
      id: polisySummaryId,
      policySummaryTerms: [],
      policySummaryRemark: lastUpdateRemarkData?.data.policySummaryRemark || "",
    },
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      try {
        const temData = {
          id: polisySummaryId,
          policySummaryTerms: policySummaryTermsPayload,
          policySummaryRemark: values.policySummaryRemark,
        };
        const addCompanyResponse = await newDescription(temData).unwrap();
        if (!addCompanyResponse.status) {
          showErrorMessage(addCompanyResponse.message);
        } else {
          showMessage(addCompanyResponse.message);
          navigate(
            `/customer/${customerId}/summary/pdf/${polisySummaryId}`
          );
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const formikStep3 = useFormik({
    initialValues: {
      id: polisySummaryId,
      policySummaryRemark: "",
    },

    onSubmit: async (values, { resetForm }) => {
      try {
        const temData = {
          id: polisySummaryId,
          policySummaryRemark: values.policySummaryRemark,
        };
        const addCompanyResponse = await newRemark(temData).unwrap();
        if (!addCompanyResponse.status) {
          showErrorMessage(addCompanyResponse.message);
        } else {
          showMessage(addCompanyResponse.message);
          resetForm();
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const handleConfirm = () => {
    formik.handleSubmit();
    const payload = {
      selectedPolicyIds: selectedPolicies,
      // uniquePolicyProductIds: uniquePolicyProductIds,
    };

    console.log("Payload:", payload);

    setActiveStep(1);
  };

  const handleConfirmStep1 = () => {
    formikStep2.handleSubmit();
    const payload = preparePayload();
    console.log("Payload:", payload);

    setActiveStep(2);
  };

  const handleConfirmStep2 = () => {
    formikStep3.handleSubmit();
    const payload = preparePayload();
    console.log("Payload:", payload);

    setActiveStep(3);
  };

  // const handleConfirmStep1 = () => {
  //   formik.handleSubmit();
  //   const payload = {
  //     selectedPoliciesDiscription: selectedPoliciesDiscription,
  //   };

  //   console.log("Payload:", payload);

  //   setActiveStep(2);
  // };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="10vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Typography color="error">
        Error fetching customer data. Please try again later.
      </Typography>
    );
  }

  if (!customer) {
    return <Typography>No customer data available.</Typography>;
  }

  const handleOkay = () => {
    setActiveStep(0);
  };

  return (
    <div>
      <Grid container spacing={3}>
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <Grid container spacing={2}>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Card className={classes.stepperCard}>
                <Grid
                  className={classes.stepperCardGrid}
                  sx={{
                    height: { gridHeight },
                    p: 2,
                  }}
                >
                  <Stepper
                    activeStep={activeStep}
                    orientation={isMobile ? "horizontal" : "horizontal"}
                    sx={{ height: "100%" }}
                  >
                    {steps.map((label, index) => (
                      <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </Grid>
              </Card>
            </Grid>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Grid container spacing={2}>
                <Grid item lg={12} md={12} sm={12} xs={12}>
                  <Card className={classes.card}>
                    <Grid container>
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        className={classes.profileBottomSetUp23}
                      >
                        <Grid
                          className={classes.profileBottomDwon}
                          onClick={() =>
                            navigate(`/profile/customer/${customer?.id}`)
                          }
                          sx={{
                            cursor: "pointer",
                          }}
                        >
                          {customer ? (
                            customer.customerImg ? (
                              <img
                                src={customer.customerImg}
                                alt="Profile"
                                className={classes.profileImage}
                              />
                            ) : (
                              <Avatar
                                alt="Travis Howard"
                                src={AvatarImg}
                                className={classes.profileImage}
                              />
                            )
                          ) : (
                            <Avatar
                              alt="Travis Howard"
                              src={AvatarImg}
                              className={classes.profileImage}
                            />
                          )}
                        </Grid>
                        <Grid
                          className={classes.buttonGroupList}
                          onClick={() =>
                            navigate(`/profile/customer/${customer?.id}`)
                          }
                          sx={{
                            cursor: "pointer",
                          }}
                        >
                          <Typography
                            variant="h4"
                            className={classes.profileDetailItemList}
                          >
                            {capitalizeFirstLetter(
                              customer.customerFirstName || ""
                            ) +
                              " " +
                              capitalizeFirstLetter(
                                customer.customerLastName || ""
                              )}
                          </Typography>
                          {customer.customerRefId && (
                            <Typography variant="subtitle1" sx={{ px: "3px" }}>
                              <span className={classes.customerDetails}>
                                {` (${customer.customerRefId})`}
                              </span>{" "}
                            </Typography>
                          )}
                        </Grid>
                        <Grid
                          container
                          spacing={2}
                          className={classes.buttonGroupList}
                        >
                          {customer.customerMobileNum && (
                            <Grid item>
                              <Typography
                                // variant="body1"
                                className={classes.contactInfoProfile}
                              >
                                {customer.customerMobileNum && (
                                  <span className={classes.iconText}>
                                    {formatPhoneNumberForDisplaySpace(
                                      customer.customerMobileNum
                                    )}
                                  </span>
                                )}
                              </Typography>
                            </Grid>
                          )}
                          {customer.customerEmail && (
                            <Grid item>
                              <Typography
                                // variant="body1"
                                className={classes.contactInfoProfile}
                              >
                                {customer.customerEmail && (
                                  <span className={classes.iconText}>
                                    {customer.customerEmail?.toLowerCase()}
                                  </span>
                                )}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>

                <Grid item lg={12} md={12} sm={12} xs={12}>
                  {activeStep === 0 ? (
                    <Grid container spacing={2}>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Card className={classes.card}>
                          <Grid
                            container
                            justifyContent={"space-between"}
                            spacing={5}
                            sx={{ borderBottom: 2, padding: 2 }}
                            className={classes.titleGrid}
                          >
                            <Grid
                              item
                              lg={12}
                              md={12}
                              sm={12}
                              xs={12}
                              pt={2}
                              alignContent={"center"}
                            >
                              <Typography className={classes.cardTitle}>
                                Select policy
                              </Typography>
                            </Grid>
                          </Grid>

                          <Grid container spacing={2} p={"24px"}>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Grid container spacing={2}>
                                {Object.entries(policyGroups).map(
                                  ([policyNumber, policies]) => {
                                    const allChildIds = policies.map(
                                      (p) => p.id
                                    );
                                    const allSelected = allChildIds.every(
                                      (id) => selectedPolicies.includes(id)
                                    );
                                    const partiallySelected =
                                      !allSelected &&
                                      allChildIds.some((id) =>
                                        selectedPolicies.includes(id)
                                      );

                                    return (
                                      <Grid
                                        item
                                        lg={12}
                                        md={12}
                                        sm={12}
                                        xs={12}
                                        key={policyNumber}
                                        sx={{
                                          border: "1px solid #ddd",
                                          borderRadius: "10px",
                                          padding: 2,
                                          mb: 2,
                                        }}
                                      >
                                        {/* Parent Checkbox */}
                                        <FormControlLabel
                                          control={
                                            <Checkbox
                                              checked={allSelected}
                                              indeterminate={partiallySelected}
                                              onChange={() =>
                                                handleParentSelect(policyNumber)
                                              }
                                            />
                                          }
                                          label={
                                            <Typography fontWeight="bold">
                                              {policyNumber} ({policies.length})
                                            </Typography>
                                          }
                                        />

                                        {/* Child Checkboxes */}
                                        <Box pl={4}>
                                          {policies.map((policy) => (
                                            <FormGroup key={policy.id}>
                                              <FormControlLabel
                                                control={
                                                  <Checkbox
                                                    checked={selectedPolicies.includes(
                                                      policy.id
                                                    )}
                                                    onChange={() =>
                                                      handleChildSelect(
                                                        policy.id
                                                      )
                                                    }
                                                  />
                                                }
                                                label={`Product Description: ${
                                                  productMap.get(
                                                    Number(
                                                      policy.policyProductId
                                                    )
                                                  ) || ""
                                                }`}
                                              />
                                            </FormGroup>
                                          ))}
                                        </Box>
                                      </Grid>
                                    );
                                  }
                                )}

                                {/* <Typography mt={2}>
                                    Selected Policy IDs:{" "}
                                    {selectedPolicies.join(", ") || "None"}
                                  </Typography> */}
                              </Grid>
                            </Grid>

                            <Grid item lg={12} md={12} sm={12} xs={12} pt={3}>
                              <Card className={classes.card}>
                                <Grid container spacing={3} p={"24px"}>
                                  <Grid
                                    item
                                    lg={12}
                                    md={12}
                                    sm={12}
                                    xs={12}
                                    sx={{
                                      display: "flex",
                                      justifyContent: "flex-end",
                                    }}
                                  >
                                    <Button
                                      variant="contained"
                                      startIcon={<CheckCircleIcon />}
                                      className={classes.addButton}
                                      onClick={handleConfirm}
                                      disabled={selectedPolicies.length === 0}
                                    >
                                      {dataIsLoading && (
                                        <Box sx={{ pr: 2, pt: 0.5 }}>
                                          <CircularProgress
                                            size={16}
                                            sx={{ color: "white" }}
                                          />
                                        </Box>
                                      )}
                                      Confirm & Next
                                    </Button>
                                  </Grid>
                                </Grid>
                              </Card>
                            </Grid>
                          </Grid>
                        </Card>
                      </Grid>
                    </Grid>
                  ) : activeStep === 1 ? (
                    <>
                      {dataIsLoading ? (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            pt: 2,
                          }}
                        >
                          <CircularProgress size={40} />
                        </Box>
                      ) : (
                        <form onSubmit={formikStep2.handleSubmit}>
                          <Grid container spacing={2}>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Grid container spacing={2}>
                                {addedPolicyTerm.map((term) => (
                                  <Grid
                                    item
                                    lg={12}
                                    md={12}
                                    sm={12}
                                    xs={12}
                                    key={term.id}
                                  >
                                    <Card
                                      sx={{
                                        padding: 2,
                                        borderRadius: "8px",
                                        boxShadow: "none",
                                      }}
                                    >
                                      <Grid
                                        container
                                        spacing={2}
                                        alignItems="center"
                                      >
                                        <Grid item>
                                          <Typography
                                            className={classes.cardTitle}
                                            sx={{ pb: "2px" }}
                                          >
                                            {productMap.get(
                                              Number(term.policyTermProductId)
                                            ) || ""}
                                          </Typography>
                                        </Grid>
                                        {term.policyTermsArray.map(
                                          (item, idx) => (
                                            <Grid
                                              item
                                              lg={12}
                                              md={12}
                                              sm={12}
                                              xs={12}
                                              key={idx}
                                            >
                                              <Grid
                                                container
                                                spacing={2}
                                                alignItems="center"
                                              >
                                                <Grid item>
                                                  <Checkbox
                                                    checked={
                                                      checkedPolicies[
                                                        `${term.id}-${item.policyTermsDescription}`
                                                      ] || false
                                                    }
                                                    onChange={() =>
                                                      handleCheckboxChange(
                                                        term.id,
                                                        item.policyTermsDescription
                                                      )
                                                    }
                                                    sx={{
                                                      transform: "scale(1.5)",
                                                      borderRadius: "8px",
                                                    }}
                                                  />
                                                </Grid>
                                                <Grid
                                                  item
                                                  lg={11}
                                                  md={11}
                                                  sm={10}
                                                  xs={10}
                                                >
                                                  {/* <Typography
                                                    className="headingSummary"
                                                    sx={{ pb: "2px" }}
                                                  >
                                                    {
                                                      item.policyTermsDescription
                                                    }
                                                  </Typography> */}

                                                  <Typography
                                                    className={
                                                      classes.bodyPrint133
                                                    }
                                                    component="div"
                                                    dangerouslySetInnerHTML={{
                                                      __html:
                                                        item.policyTermsDescription,
                                                    }}
                                                  />
                                                </Grid>
                                              </Grid>
                                            </Grid>
                                          )
                                        )}
                                      </Grid>
                                    </Card>
                                  </Grid>
                                ))}
                              </Grid>
                            </Grid>

                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Card className={classes.card}>
                                <Grid
                                  container
                                  justifyContent={"space-between"}
                                  spacing={5}
                                  sx={{ borderBottom: 2, padding: 2 }}
                                  className={classes.titleGrid}
                                >
                                  <Grid
                                    item
                                    lg={12}
                                    md={12}
                                    sm={12}
                                    xs={12}
                                    pt={2}
                                    alignContent={"center"}
                                  >
                                    <Typography className={classes.cardTitle}>
                                      Add More Terms
                                    </Typography>
                                  </Grid>
                                </Grid>

                                <Grid container spacing={2} p={"24px"}>
                                  <Grid item lg={12} md={12} sm={12} xs={12}>
                                    {/* <TextField
                                      placeholder="Enter More Terms"
                                      size="small"
                                      multiline
                                      rows={8}
                                      {...formikStep2.getFieldProps(
                                        "policySummaryRemark"
                                      )}
                                      className={classes.textField}
                                      InputProps={{
                                        sx: {
                                          fontSize: 14,
                                        },
                                      }}
                                      InputLabelProps={{
                                        sx: {
                                          fontSize: 14,
                                        },
                                      }}
                                    /> */}

                                    <ReactQuill
                                      value={
                                        formikStep2.values.policySummaryRemark
                                      }
                                      onChange={(value) =>
                                        formikStep2.setFieldValue(
                                          "policySummaryRemark",
                                          value
                                        )
                                      }
                                      placeholder="Enter More Terms..."
                                      theme="snow"
                                      style={{
                                        height: "400px",
                                        marginBottom: "20px",
                                      }}
                                      modules={{
                                        toolbar: [
                                          [{ font: [] }],
                                          [
                                            {
                                              header: [1, 2, 3, 4, 5, 6, false],
                                            },
                                          ],
                                          [
                                            "bold",
                                            "italic",
                                            "underline",
                                            "strike",
                                          ],
                                          [{ color: [] }, { background: [] }],
                                          [
                                            { script: "sub" },
                                            { script: "super" },
                                          ],
                                          [
                                            { list: "ordered" },
                                            { list: "bullet" },
                                          ],
                                          [{ indent: "-1" }, { indent: "+1" }],
                                          [{ direction: "rtl" }],
                                          [{ align: [] }],
                                          ["blockquote", "code-block"],
                                          ["link", "image", "video"],
                                          ["clean"],
                                        ],
                                      }}
                                    />
                                  </Grid>
                                </Grid>
                              </Card>
                            </Grid>

                            <Grid item lg={12} md={12} sm={12} xs={12} pt={3}>
                              <Card className={classes.card}>
                                <Grid container spacing={3} p={"24px"}>
                                  <Grid
                                    item
                                    lg={12}
                                    md={12}
                                    sm={12}
                                    xs={12}
                                    sx={{
                                      display: "flex",
                                      justifyContent: "flex-end",
                                    }}
                                  >
                                    <Button
                                      variant="contained"
                                      startIcon={<CheckCircleIcon />}
                                      className={classes.addButton}
                                      onClick={handleConfirmStep1}
                                      // disabled={Object.values(
                                      //   checkedPolicies
                                      // ).every((isChecked) => !isChecked)}
                                    >
                                      Confirm & Next
                                    </Button>
                                  </Grid>
                                </Grid>
                              </Card>
                            </Grid>
                          </Grid>
                        </form>
                      )}
                    </>
                  ) : (
                    <Grid
                      container
                      direction="row"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Card className={classes.card}>
                          <Grid
                            container
                            justifyContent={"space-between"}
                            spacing={5}
                            sx={{ padding: 2, height: "240px" }}
                            className={classes.titleGrid}
                          >
                            <Grid
                              item
                              lg={12}
                              md={12}
                              sm={12}
                              xs={12}
                              py={2}
                              alignContent={"center"}
                              textAlign={"center"}
                            >
                              <Typography className={classes.cardTitle} p={2}>
                                Generate Summary Report
                              </Typography>
                              <Button
                                variant="contained"
                                startIcon={<LocalPrintshopSharpIcon />}
                                className={classes.addButton}
                                onClick={() =>
                                  navigate(
                                    `/customer/${customerId}/summary/pdf/${polisySummaryId}`
                                  )
                                }
                                disabled={isLoading}
                              >
                                Report
                              </Button>
                            </Grid>
                          </Grid>
                        </Card>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default Index;
